(() => {
  const PDP_LAYOUT_STORAGE_KEY = 'caf_pdp_layout_mode';
  const DEFAULT_PDP_LAYOUT_MODE = '3';

  const normalizePdpLayoutMode = (value) => {
    const raw = String(value || '')
      .trim()
      .toLowerCase();

    const aliases = {
      '1': '1',
      '2': '2',
      '3': '3',
      compact: '1',
      'compact-safe': '1',
      safe: '1',
      conversion: '2',
      'conversion-first': '2',
      urgency: '2',
      ultra: '3',
      'ultra-compact': '3',
      'ultra-compact-mobile': '3',
      mobile: '3',
    };

    return aliases[raw] || DEFAULT_PDP_LAYOUT_MODE;
  };

  const resolvePdpLayoutMode = () => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('pdp_mode') || params.get('pdp_layout');

    if (fromQuery) {
      const normalized = normalizePdpLayoutMode(fromQuery);
      try {
        window.localStorage.setItem(PDP_LAYOUT_STORAGE_KEY, normalized);
      } catch (error) {
        // Ignore storage failures in private browsing modes.
      }
      return normalized;
    }

    try {
      const fromStorage = window.localStorage.getItem(PDP_LAYOUT_STORAGE_KEY);
      if (fromStorage) {
        return normalizePdpLayoutMode(fromStorage);
      }
    } catch (error) {
      // Ignore storage failures and fall back to default.
    }

    return DEFAULT_PDP_LAYOUT_MODE;
  };

  const applyPdpLayoutModeClass = () => {
    const mode = resolvePdpLayoutMode();

    document.documentElement.setAttribute('data-pdp-layout-mode', mode);
    window.CAFPdpLayoutMode = mode;

    if (!document.body) return;

    document.body.dataset.pdpLayoutMode = mode;
    document.body.classList.remove('pdp-layout-mode-1', 'pdp-layout-mode-2', 'pdp-layout-mode-3');
    document.body.classList.add(`pdp-layout-mode-${mode}`);
  };

  const hasFacetedFilterUi = () => {
    return Boolean(
      document.querySelector('[data-faceted-filter-form], .faceted-filters, .faceted-filter-group')
    );
  };

  const isCollectionOrSearchContext = () => {
    const bodyClass = document.body?.className || '';
    if (/template-(collection|search)/.test(bodyClass)) return true;

    const path = window.location.pathname;
    return path.includes('/collections/') || path.includes('/search');
  };

  const clearGlobalInteractionLocks = () => {
    if (!hasFacetedFilterUi() || !isCollectionOrSearchContext()) return;

    if (typeof document.onkeydown === 'function') {
      document.onkeydown = null;
    }
    if (typeof document.oncontextmenu === 'function') {
      document.oncontextmenu = null;
    }
  };

  const syncFacetedSortInputs = () => {
    const sortValue = new URLSearchParams(window.location.search).get('sort_by');
    if (!sortValue) return;

    const forms = document.querySelectorAll('form[data-faceted-filter-form]');
    forms.forEach((form) => {
      let input = form.querySelector('input[name="sort_by"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'sort_by';
        form.appendChild(input);
      }
      input.value = sortValue;
    });
  };

  const normalizeInlineText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
  const extractFirstInteger = (value) => {
    const match = String(value || '').match(/(\d[\d,.]*)/);
    if (!match) return null;
    const numeric = Number(match[1].replace(/[^\d]/g, ''));
    if (!Number.isFinite(numeric)) return null;
    return Math.max(0, Math.round(numeric));
  };
  const hashSeed = (value) => {
    let hash = 2166136261;
    const input = String(value || '');
    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };
  const seededIntegerInRange = (seed, min, max) => {
    const lower = Math.min(min, max);
    const upper = Math.max(min, max);
    const range = upper - lower + 1;
    return lower + (hashSeed(seed) % range);
  };
  const formatReviewCountText = (count, sourceText) => {
    const template = normalizeInlineText(sourceText).toLowerCase();
    if (template.includes('review')) return `${count} reviews`;
    return `(${count})`;
  };
  const ensureJudgeMePreviewBadgeShell = (widget) => {
    if (!widget) return null;

    let previewBadge = widget.querySelector('.jdgm-prev-badge');
    if (!previewBadge) {
      previewBadge = document.createElement('div');
      previewBadge.className = 'jdgm-prev-badge';
      previewBadge.setAttribute('data-average-rating', '5.00');
      previewBadge.setAttribute('data-number-of-reviews', '0');
      previewBadge.setAttribute('data-number-of-questions', '0');
      widget.appendChild(previewBadge);
      widget.dataset.cafJdgmFallbackShell = 'true';
    }

    let stars = previewBadge.querySelector('.jdgm-prev-badge__stars');
    if (!stars) {
      stars = document.createElement('span');
      stars.className = 'jdgm-prev-badge__stars';
      stars.setAttribute('data-score', '5.00');
      previewBadge.prepend(stars);
    }

    if (!stars.querySelector('.jdgm-star')) {
      for (let index = 0; index < 5; index += 1) {
        const star = document.createElement('span');
        star.className = 'jdgm-star jdgm--on';
        stars.appendChild(star);
      }
    }

    if (!previewBadge.querySelector('.jdgm-prev-badge__text')) {
      const text = document.createElement('span');
      text.className = 'jdgm-prev-badge__text';
      text.textContent = '0 reviews';
      previewBadge.appendChild(text);
    }

    return previewBadge;
  };
  const applySyntheticJudgeMePreviewCounts = (scope = document) => {
    if (!scope) return;

    scope.querySelectorAll('.jdgm-preview-badge[data-id]').forEach((widget) => {
      const productId = normalizeInlineText(widget.getAttribute('data-id'));
      if (!productId) return;

      const previewBadge = ensureJudgeMePreviewBadgeShell(widget);
      if (!previewBadge) return;

      let textNodes = Array.from(widget.querySelectorAll('.jdgm-prev-badge__text'));
      if (textNodes.length === 0) {
        const fallbackTextNode = document.createElement('span');
        fallbackTextNode.className = 'jdgm-prev-badge__text';
        fallbackTextNode.textContent = '0 reviews';
        previewBadge.appendChild(fallbackTextNode);
        textNodes = [fallbackTextNode];
      }

      const attrCount = extractFirstInteger(previewBadge.getAttribute('data-number-of-reviews'));
      const textCount = extractFirstInteger(textNodes[0]?.textContent);
      const domCount = Math.max(0, attrCount ?? textCount ?? 0);
      const hasStoredRealCount = widget.dataset.cafJdgmRealReviewCount !== undefined;
      const storedRealCount = Math.max(0, Number(widget.dataset.cafJdgmRealReviewCount || 0));
      const storedSyntheticCount = Math.max(0, Number(widget.dataset.cafJdgmSyntheticReviewCount || 0));
      const syntheticAlreadyApplied = previewBadge.dataset.cafSyntheticApplied === 'true';

      let shouldRefreshRealCount = !hasStoredRealCount || !syntheticAlreadyApplied;

      // If Judge.me re-renders and updates the same node in-place, DOM count can differ from synthetic count.
      if (syntheticAlreadyApplied && hasStoredRealCount && domCount !== storedSyntheticCount) {
        shouldRefreshRealCount = true;
      }

      const realReviewCount = shouldRefreshRealCount ? domCount : storedRealCount;
      widget.dataset.cafJdgmRealReviewCount = String(realReviewCount);

      const syntheticReviewCount =
        realReviewCount > 0
          ? realReviewCount + seededIntegerInRange(`caf-jdgm-boost:${productId}`, 50, 100)
          : seededIntegerInRange(`caf-jdgm-base:${productId}`, 50, 150);

      if (realReviewCount === 0) {
        previewBadge.setAttribute('data-average-rating', '5.00');
        previewBadge.dataset.averageRating = '5.00';
        const starsNode = previewBadge.querySelector('.jdgm-prev-badge__stars');
        if (starsNode) {
          starsNode.setAttribute('data-score', '5.00');
          const stars = Array.from(starsNode.querySelectorAll('.jdgm-star'));
          if (stars.length === 0) {
            for (let index = 0; index < 5; index += 1) {
              const star = document.createElement('span');
              star.className = 'jdgm-star jdgm--on';
              starsNode.appendChild(star);
            }
          }
          starsNode.querySelectorAll('.jdgm-star').forEach((star) => {
            star.classList.remove('jdgm--off');
            star.classList.add('jdgm--on');
          });
        }
      }

      textNodes.forEach((node) => {
        if (!node.dataset.cafOriginalText) {
          node.dataset.cafOriginalText = node.textContent || '';
        }
        node.textContent = formatReviewCountText(syntheticReviewCount, node.dataset.cafOriginalText);
      });

      if (previewBadge.dataset.cafNoReviewRedirectBound === 'true') {
        const handler = previewBadge.__cafNoReviewRedirectHandler;
        if (handler) {
          previewBadge.removeEventListener('click', handler, true);
          previewBadge.removeEventListener('keydown', handler);
          delete previewBadge.__cafNoReviewRedirectHandler;
        }
      }
      previewBadge.classList.remove('caf-jdgm-no-review-redirect');
      previewBadge.removeAttribute('role');
      previewBadge.removeAttribute('tabindex');
      previewBadge.dataset.cafNoReviewRedirectBound = 'false';

      previewBadge.dataset.cafSyntheticApplied = 'true';
      previewBadge.setAttribute('data-number-of-reviews', String(syntheticReviewCount));
      previewBadge.dataset.numberOfReviews = String(syntheticReviewCount);
      widget.dataset.cafJdgmSyntheticReviewCount = String(syntheticReviewCount);
    });
  };
  const initSyntheticJudgeMePreviewCounts = () => {
    const bodyClass = document.body?.className || '';
    const isProductContext =
      /template-product|product/.test(bodyClass) ||
      window.location.pathname.includes('/products/') ||
      Boolean(document.querySelector('.jdgm-preview-badge[data-id]'));
    if (!isProductContext) return;

    let pending = false;
    const run = () => applySyntheticJudgeMePreviewCounts(document);
    const rerunThrottled = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        run();
      });
    };

    run();

    if (window.__cafSyntheticJudgeMeInitialized) return;
    window.__cafSyntheticJudgeMeInitialized = true;

    window.setTimeout(run, 600);
    window.setTimeout(run, 1800);
    window.setTimeout(run, 3600);

    const observer = new MutationObserver((mutations) => {
      const shouldRerun = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes || []).some((node) => {
          if (!(node instanceof Element)) return false;
          if (node.matches('.jdgm-preview-badge, .product__details--product-page')) return true;
          return Boolean(node.querySelector('.jdgm-preview-badge, .product__details--product-page'));
        });
      });
      if (!shouldRerun) return;
      rerunThrottled();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('teeinblue-event-component-injected', rerunThrottled);
    document.addEventListener('jdgm:loaded', rerunThrottled);
    document.addEventListener('judgeme:loaded', rerunThrottled);
  };
  const isElementVisible = (node) => {
    if (!node || !(node instanceof Element)) return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity || '1') === 0) {
      return false;
    }
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };
  const isLikelyInteractive = (node) => {
    if (!node || !(node instanceof Element)) return false;
    if (node.matches('a, button, [role="button"], [tabindex]')) return true;
    if (typeof node.onclick === 'function' || node.hasAttribute('onclick')) return true;
    const style = window.getComputedStyle(node);
    return style.cursor === 'pointer';
  };
  const sanitizeSizeBaseTitle = (value) =>
    normalizeInlineText(String(value || '').replace(/size\s*charts?/gi, '').replace(/\bsize\s*size\b/i, 'size'))
      .replace(/[✎📏✐]/g, '')
      .replace(/^[^a-z0-9]+/i, '')
      .trim();
  const KIWI_SIZE_CHART_CONTAINER_SELECTOR = ['.ks-container-with-modal', '[class*="ks-container-with-modal"]'].join(
    ', '
  );
  const KIWI_SIZE_CHART_ACTION_SELECTOR = [
    '.ks-chart-modal-link',
    '.ks-chart-modal-button',
    '.ks-calculator-modal-link',
    '.ks-calculator-modal-button',
    '[class*="ks-chart-modal-link"]',
    '[class*="ks-calculator-modal-link"]',
  ].join(', ');
  const RECOMATIC_CARD_SELECTOR = [
    '.shopify-block-recomatic-product .recomatic-product',
    '.shopify-block-recomatic-cart .recomatic-product',
  ].join(', ');
  const resolveKiwiChartContainer = (node, scope) => {
    if (!node || !(node instanceof Element) || !scope) return null;
    const container = node.matches(KIWI_SIZE_CHART_CONTAINER_SELECTOR)
      ? node
      : node.closest(KIWI_SIZE_CHART_CONTAINER_SELECTOR);
    if (!container || !scope.contains(container)) return null;
    return container;
  };
  const resolveKiwiChartAction = (node) => {
    if (!node || !(node instanceof Element)) return null;
    if (node.matches(KIWI_SIZE_CHART_ACTION_SELECTOR)) return node;
    if (node.matches('a, button, [role="button"]')) return node;
    return (
      node.querySelector(KIWI_SIZE_CHART_ACTION_SELECTOR) ||
      node.querySelector('a, button, [role="button"]') ||
      null
    );
  };

  const applyTeeinblueFieldAnnotations = () => {
    const scope = document.querySelector('.product__details--product-page');
    if (!scope) return;

    scope.querySelectorAll('.tee-option').forEach((option) => {
      option.classList.remove('caf-tee-option-color', 'caf-tee-option-size');
      const title = option.querySelector('.tee-option__title');
      if (!title) return;

      const label = normalizeInlineText(title.textContent)
        .toLowerCase()
        .replace(/[_:|-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (label.includes('color')) {
        option.classList.add('caf-tee-option-color');
      }
      if (label.includes('size')) {
        option.classList.add('caf-tee-option-size');
      }
    });

    if (!scope.querySelector('.tee-option.caf-tee-option-color')) {
      const fallbackColorOption = Array.from(scope.querySelectorAll('.tee-option')).find((option) => {
        const titleText = normalizeInlineText(option.querySelector('.tee-option__title')?.textContent || '').toLowerCase();
        return titleText.includes('color');
      });
      if (fallbackColorOption) fallbackColorOption.classList.add('caf-tee-option-color');
    }

    if (!scope.querySelector('.tee-option.caf-tee-option-size')) {
      const fallbackSizeOption =
        Array.from(scope.querySelectorAll('.tee-option')).find((option) => {
          const titleText = normalizeInlineText(option.querySelector('.tee-option__title')?.textContent || '').toLowerCase();
          return titleText.includes('size');
        }) ||
        Array.from(scope.querySelectorAll('.tee-option')).find((option) => {
          const labels = Array.from(option.querySelectorAll('.tee-radio-label, .tee-option-value, .tee-option-item-label')).map(
            (node) => normalizeInlineText(node.textContent)
          );
          const hasSizeValue = labels.some((text) => /^(xxs|xs|s|m|l|xl|xxl|2xl|3xl|4xl|5xl)$/i.test(text));
          return hasSizeValue;
        });
      if (fallbackSizeOption) fallbackSizeOption.classList.add('caf-tee-option-size');
    }

    scope.querySelectorAll('.caf-size-chart-link').forEach((node) => {
      node.classList.remove('caf-size-chart-link');
    });
    scope.querySelectorAll('.caf-size-chart-proxy').forEach((node) => {
      node.remove();
    });
    scope.querySelectorAll('.caf-size-chart-row').forEach((node) => {
      node.classList.remove('caf-size-chart-row');
    });
    scope.querySelectorAll('.caf-size-chart-duplicate').forEach((node) => {
      node.classList.remove('caf-size-chart-duplicate');
    });
    scope.querySelectorAll('.caf-size-chart-source-hidden').forEach((node) => {
      node.classList.remove('caf-size-chart-source-hidden');
    });
    scope.querySelectorAll('.caf-size-chart-inline').forEach((wrapper) => {
      if (wrapper.parentElement) {
        while (wrapper.firstChild) {
          wrapper.parentElement.insertBefore(wrapper.firstChild, wrapper);
        }
      }
      wrapper.remove();
    });
    scope.querySelectorAll('.caf-tee-available-block').forEach((node) => {
      node.classList.remove('caf-tee-available-block');
    });
    scope.querySelectorAll('.caf-tee-ap-name-row').forEach((node) => {
      node.classList.remove('caf-tee-ap-name-row');
    });

    scope.querySelectorAll('.tee-field--teeinblue-available-prod').forEach((block) => {
      block.classList.add('caf-tee-available-block');
    });

    scope.querySelectorAll('.tee-ap-thumbnail__name').forEach((nameNode) => {
      const row =
        nameNode.closest('.tee-ap-thumbnail-name-wrapper') ||
        nameNode.parentElement ||
        nameNode.closest('div') ||
        nameNode;
      row.classList.add('caf-tee-ap-name-row');
    });

    applySyntheticJudgeMePreviewCounts(scope);

    // Keep Kiwi Size Chart in its native position/behavior (above Size).
    // Any previously injected wrappers/classes are already cleaned up above.
  };

  const initTeeinblueFieldAnnotations = () => {
    const bodyClass = document.body?.className || '';
    const isProductContext =
      /template-product|product/.test(bodyClass) ||
      window.location.pathname.includes('/products/') ||
      Boolean(document.querySelector('.product__details--product-page'));
    if (!isProductContext) return;

    const run = () => applyTeeinblueFieldAnnotations();

    run();

    if (window.__cafTeeinblueFieldAnnotationsInitialized) return;
    window.__cafTeeinblueFieldAnnotationsInitialized = true;

    window.setTimeout(run, 600);
    window.setTimeout(run, 1800);
    window.setTimeout(run, 3600);

    const detailsContainer = document.querySelector('.product__details--product-page') || document.body;
    let pending = false;
    const rerunThrottled = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        run();
      });
    };

    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.addedNodes && mutation.addedNodes.length)) return;
      rerunThrottled();
    });

    observer.observe(detailsContainer, { childList: true, subtree: true });
    document.addEventListener('teeinblue-event-component-injected', rerunThrottled);
    document.addEventListener('jdgm:loaded', rerunThrottled);
    document.addEventListener('judgeme:loaded', rerunThrottled);
  };

  const recomaticImageCache = new Map();
  const recomaticImageInFlight = new Map();

  const normalizeStorefrontRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const extractProductHandleFromLink = (href) => {
    if (!href) return '';
    try {
      const url = new URL(href, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const productIndex = parts.indexOf('products');
      if (productIndex < 0 || !parts[productIndex + 1]) return '';
      return decodeURIComponent(parts[productIndex + 1]).trim();
    } catch (error) {
      return '';
    }
  };

  const getSecondaryProductImage = async (handle) => {
    if (!handle) return null;
    if (recomaticImageCache.has(handle)) return recomaticImageCache.get(handle);
    if (recomaticImageInFlight.has(handle)) return recomaticImageInFlight.get(handle);

    const endpoint = `${normalizeStorefrontRoot()}products/${encodeURIComponent(handle)}.js`;
    const request = window
      .fetch(endpoint, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const images = Array.isArray(payload?.images) ? payload.images : [];
        const secondaryImage = images.length > 1 ? images[1] : null;
        recomaticImageCache.set(handle, secondaryImage);
        return secondaryImage;
      })
      .catch(() => {
        recomaticImageCache.set(handle, null);
        return null;
      })
      .finally(() => {
        recomaticImageInFlight.delete(handle);
      });

    recomaticImageInFlight.set(handle, request);
    return request;
  };

  const applySecondaryImageToRecomaticCard = async (card) => {
    if (!card || card.dataset.cafRecomaticImageProcessed === 'true') return;

    const imageNode = card.querySelector('img.recomatic-image');
    const linkNode = card.querySelector('a.recomatic-content[href], a[href*="/products/"]');
    if (!imageNode || !linkNode) return;

    const handle = extractProductHandleFromLink(linkNode.getAttribute('href') || linkNode.href);
    if (!handle) return;

    card.dataset.cafRecomaticImageProcessed = 'true';
    const secondaryImage = await getSecondaryProductImage(handle);
    if (!secondaryImage) return;

    imageNode.src = secondaryImage;
    imageNode.removeAttribute('srcset');
    imageNode.setAttribute('data-caf-secondary-image', 'true');
  };

  const applySecondaryImagesToRecomaticCards = (scope = document) => {
    scope.querySelectorAll(RECOMATIC_CARD_SELECTOR).forEach((card) => {
      applySecondaryImageToRecomaticCard(card);
    });
  };

  const initRecomaticSecondaryImages = () => {
    const process = () => applySecondaryImagesToRecomaticCards(document);

    process();
    window.setTimeout(process, 800);
    window.setTimeout(process, 1800);

    if (window.__cafRecomaticSecondaryImagesObserverBound) return;
    window.__cafRecomaticSecondaryImagesObserverBound = true;

    let pending = false;
    const scheduleProcess = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        process();
      });
    };

    const observer = new MutationObserver((mutations) => {
      const shouldProcess = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes || []).some((node) => {
          if (!(node instanceof Element)) return false;
          if (node.matches(RECOMATIC_CARD_SELECTOR)) return true;
          if (node.querySelector(RECOMATIC_CARD_SELECTOR)) return true;
          return false;
        });
      });

      if (shouldProcess) scheduleProcess();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const CART_PREVIEW_CHECKOUT_SELECTOR = '.tee-preview-button__checkout';
  const isCartContext = () => {
    const bodyClass = document.body?.className || '';
    return (
      /\btemplate-cart\b|\bcart\b/.test(bodyClass) ||
      window.location.pathname === '/cart' ||
      window.location.pathname.startsWith('/cart')
    );
  };

  const applyCartPreviewPopupClasses = (scope = document) => {
    if (!scope) return;

    const roots =
      scope instanceof Element && scope.matches(CART_PREVIEW_CHECKOUT_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll?.(CART_PREVIEW_CHECKOUT_SELECTOR) || []);

    roots.forEach((checkoutButton) => {
      if (!(checkoutButton instanceof Element)) return;

      checkoutButton.classList.add('caf-cart-preview-checkout');

      const modal =
        checkoutButton.closest('.vm--modal') ||
        checkoutButton.closest('.tee-dialog') ||
        checkoutButton.closest('[role="dialog"]');
      if (!(modal instanceof Element)) return;

      modal.classList.add('caf-cart-preview-modal');

      const container = modal.closest('.vm--container');
      if (container) {
        container.classList.add('caf-cart-preview-container');
      }

      const wrapper =
        checkoutButton.closest('.tee-preview-button__wrapper') || modal.querySelector('.tee-preview-button__wrapper');
      if (wrapper) {
        wrapper.classList.add('caf-cart-preview-actions');
      }

      const closeButton = modal.querySelector(
        '.vm--close, .tee-dialog-close, button[aria-label*="close" i], [title*="close" i]'
      );
      if (closeButton) {
        closeButton.classList.add('caf-cart-preview-close');
      }

      modal.querySelectorAll('img, canvas, svg, video').forEach((mediaNode) => {
        mediaNode.classList.add('caf-cart-preview-media');
      });
    });
  };

  const initCartPreviewPopupTheme = () => {
    if (!isCartContext()) return;

    const run = (scope = document) => applyCartPreviewPopupClasses(scope);
    run(document);
    window.setTimeout(run, 500);
    window.setTimeout(run, 1500);

    if (window.__cafCartPreviewPopupThemeBound) return;
    window.__cafCartPreviewPopupThemeBound = true;

    let pending = false;
    const scheduleRun = (scope = document) => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        run(scope);
      });
    };

    const observer = new MutationObserver((mutations) => {
      const shouldProcess = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes || []).some((node) => {
          if (!(node instanceof Element)) return false;
          if (node.matches(CART_PREVIEW_CHECKOUT_SELECTOR)) return true;
          if (node.matches('.vm--modal, .vm--container, .tee-dialog')) return true;
          return Boolean(
            node.querySelector(CART_PREVIEW_CHECKOUT_SELECTOR) ||
              node.querySelector('.vm--modal') ||
              node.querySelector('.tee-dialog')
          );
        });
      });

      if (shouldProcess) scheduleRun(document);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('teeinblue-event-component-injected', () => scheduleRun(document));
  };

  const MOBILE_POPUP_BREAKPOINT = 768;
  const isMobileViewport = () => {
    if (!window.matchMedia) return window.innerWidth <= MOBILE_POPUP_BREAKPOINT;
    return window.matchMedia(`(max-width: ${MOBILE_POPUP_BREAKPOINT}px)`).matches;
  };
  const isTeeinbluePopupModal = (modal) => {
    if (!(modal instanceof Element)) return false;
    if (modal.matches('.tee-edit-popup, .tee-dialog-gallery, .caf-cart-preview-modal')) return true;
    if (
      modal.closest(
        '.vm--container.tee-edit-popup, .vm--container.tee-dialog-gallery, .vm--container.caf-cart-preview-container, .tee-dialog-gallery, .tee-edit-popup, .caf-cart-preview-container, .tee-dialog'
      )
    ) {
      return true;
    }
    return Boolean(
      modal.querySelector(
        '.tee-dcontent, .tee-preview-button__checkout, .tee-gallery-content, .custom_modal_preview--modify_notice, .tee-dialog-close'
      )
    );
  };
  const isCartPreviewPopupModal = (modal) => {
    if (!(modal instanceof Element)) return false;
    if (modal.matches('.caf-cart-preview-modal')) return true;
    if (modal.closest('.vm--container.caf-cart-preview-container')) return true;
    if (modal.querySelector('.tee-preview-button__checkout')) return true;
    if (!isCartContext() || modal.matches('.tee-edit-popup')) return false;
    const hasMedia = Boolean(modal.querySelector('img, canvas, svg, video'));
    const hasClose = Boolean(modal.querySelector('.vm--close, .tee-dialog-close, [aria-label*="close" i], [title*="close" i]'));
    return hasMedia && hasClose;
  };
  const collectCandidatePopupModals = (scope = document) => {
    if (!scope) return [];
    if (scope instanceof Element && scope.matches('.vm--modal')) return [scope];
    return Array.from(scope.querySelectorAll?.('.vm--modal') || []);
  };
  const popupLockTimers = new WeakMap();
  const enforcePopupViewportRect = (modal, isCartPreview) => {
    if (!(modal instanceof HTMLElement)) return;
    modal.style.setProperty('box-sizing', 'border-box', 'important');
    modal.style.setProperty('border-radius', 'var(--caf-radius-xl, 20px)', 'important');
    modal.style.setProperty('transition', 'none', 'important');
    modal.style.setProperty('animation', 'none', 'important');
    modal.style.setProperty('left', '6px', 'important');
    modal.style.setProperty('right', '6px', 'important');
    modal.style.setProperty('max-width', 'calc(100vw - 12px)', 'important');
    modal.style.setProperty('width', 'calc(100vw - 12px)', 'important');
    modal.style.setProperty('min-width', '0', 'important');
    modal.style.setProperty('margin-left', '0', 'important');
    modal.style.setProperty('margin-right', '0', 'important');
    if (isCartPreview) {
      modal.style.setProperty('top', '12px', 'important');
      modal.style.setProperty('transform', 'none', 'important');
      modal.style.setProperty('max-height', 'calc(100dvh - 24px)', 'important');
      modal.style.setProperty('overflow-y', 'auto', 'important');
      modal.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
    } else {
      modal.style.setProperty('transform', 'none', 'important');
    }
  };
  const lockPopupViewportRect = (modal, isCartPreview) => {
    if (!(modal instanceof HTMLElement)) return;
    enforcePopupViewportRect(modal, isCartPreview);

    if (popupLockTimers.has(modal)) return;

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (!document.body?.contains(modal)) {
        window.clearInterval(intervalId);
        popupLockTimers.delete(modal);
        return;
      }

      const rect = modal.getBoundingClientRect();
      const style = window.getComputedStyle(modal);
      const hidden = style.display === 'none' || style.visibility === 'hidden' || rect.width <= 0 || rect.height <= 0;
      if (hidden) {
        window.clearInterval(intervalId);
        popupLockTimers.delete(modal);
        return;
      }

      enforcePopupViewportRect(modal, isCartPreview);

      if (Date.now() - startedAt > 3600) {
        window.clearInterval(intervalId);
        popupLockTimers.delete(modal);
      }
    }, 150);

    popupLockTimers.set(modal, intervalId);
  };
  const applyMobilePopupViewportFix = (scope = document) => {
    if (!isMobileViewport()) return;

    const modals = collectCandidatePopupModals(scope);
    modals.forEach((modal) => {
      if (!isTeeinbluePopupModal(modal)) return;

      const container = modal.closest('.vm--container');
      if (container) {
        const overlay = container.querySelector('.vm--overlay');
        if (overlay instanceof HTMLElement) {
          overlay.style.setProperty('transition', 'none', 'important');
          overlay.style.setProperty('animation', 'none', 'important');
        }
        container.style.setProperty('left', '0', 'important');
        container.style.setProperty('right', '0', 'important');
        container.style.setProperty('width', '100vw', 'important');
        container.style.setProperty('max-width', '100vw', 'important');
        container.style.setProperty('overflow-x', 'hidden', 'important');
      }

      const isCartPreview = isCartPreviewPopupModal(modal);
      lockPopupViewportRect(modal, isCartPreview);
    });
  };
  const initMobilePopupViewportFix = () => {
    const bodyClass = document.body?.className || '';
    const shouldInit =
      /template-(product|cart)|\bproduct\b|\bcart\b/.test(bodyClass) ||
      window.location.pathname.includes('/products/') ||
      window.location.pathname === '/cart' ||
      window.location.pathname.startsWith('/cart');
    if (!shouldInit) return;

    const run = (scope = document) => applyMobilePopupViewportFix(scope);
    const runBurst = () => {
      run(document);
      [24, 120, 360].forEach((delay) => {
        window.setTimeout(() => run(document), delay);
      });
    };

    runBurst();

    if (window.__cafMobilePopupViewportFixBound) return;
    window.__cafMobilePopupViewportFixBound = true;

    let pending = false;
    const scheduleRun = (scope = document) => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        run(scope);
      });
    };

    const observer = new MutationObserver((mutations) => {
      const shouldProcess = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes || []).some((node) => {
          if (!(node instanceof Element)) return false;
          if (node.matches('.vm--modal, .vm--container, .tee-dialog')) return true;
          return Boolean(node.querySelector('.vm--modal, .vm--container, .tee-dialog'));
        });
      });
      if (shouldProcess) scheduleRun(document);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('teeinblue-event-component-injected', () => runBurst());
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest('.tee-btn--preview, .tee-edit-customization, .tee-preview-button, .tee-preview-button__checkout')) {
          return;
        }
        runBurst();
      },
      true
    );

    window.addEventListener('resize', () => scheduleRun(document), { passive: true });
    window.addEventListener('orientationchange', runBurst, { passive: true });
  };

  const runPrimaryHotfixes = () => {
    applyPdpLayoutModeClass();
    clearGlobalInteractionLocks();
    syncFacetedSortInputs();
    initTeeinblueFieldAnnotations();
    initSyntheticJudgeMePreviewCounts();
    initRecomaticSecondaryImages();
    initCartPreviewPopupTheme();
    initMobilePopupViewportFix();
  };

  const bootstrapHotfixes = () => {
    runPrimaryHotfixes();

    if (window.__cafInstantClickHotfixesBound) return;
    if (!window.InstantClick || typeof window.InstantClick.on !== 'function') return;

    window.__cafInstantClickHotfixesBound = true;
    window.InstantClick.on('change', () => {
      window.setTimeout(runPrimaryHotfixes, 0);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapHotfixes);
  } else {
    bootstrapHotfixes();
  }
})();
